import { reactive, onBeforeMount, watch, ref } from '@vue/composition-api';

import useDate from '@/utils/hooks/useDate';
import usePagination from '@/views/components/pagination/usePagination';
import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';

import { groupBannerModel } from '@/models/banner/banner';
import store from '@/store';

import usePermissionByAccount from "@/constants/navigation/BOYNYKJMS2EH/permission-by-account"

export default function useBannerGroup() {
  const { getPermissionModule } = usePermissionByAccount()
  const { resolveDateWithSeperator } = useDate();
  const { pagination } = usePagination();
  const {
    fetchGroupBannerListModel,
    editGroupBannerModel,
    deleteGroupBannerModel,
  } = groupBannerModel();
  const { openDefaultErrorModal, openWarningModal } = useValidationModal();
  const selectedGroupBanner = reactive({ row: null, index: null });

  const {
    searchValue,
    filters,
    ordering,
    onSearch,
    sortingChanged,
  } = useFilterHeaderTable();

  const loading = reactive({ api: false });

  const fields = reactive({
    bannerGroup: [
      { key: 'seq', label: 'No.' },
      { key: 'group_banner_name', label: 'Group Name', sortable: true },
      { key: 'description', label: 'Description' },
      { key: 'valid_from', label: 'Valid From' },
      { key: 'valid_through', label: 'Valid Through' },
      { key: 'latest_update', label: 'Latest Update' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: '' },
    ],
    bannerGroupDetail: [
      { key: 'no', label: 'No.' },
      { key: 'banner_image_url', label: 'Image' },
      { key: 'set_name', label: 'Banner Set' },
      { key: 'banner_name', label: 'Banner Name' },
      { key: 'call_to_action', label: 'Call to Action' },
      { key: 'valid_from', label: 'Valid From' },
      { key: 'valid_through', label: 'Valid Through' },
      { key: 'latest_update', label: 'Latest Update' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: '' },
    ],
  });

  const groupBannerActionList = {
    edit: { label: 'Edit', value: 0 },
    assignGroup: { label: 'Assign Group', value: 1 },
    delete: { label: 'Delete', value: 2 },
  };

  const groupBannerTable = reactive({
    payload: [],
  });

  const fetchGroupBannerList = (loadingEffect = true) => {
    if (loadingEffect) {
      loading.api = true;
    }

    fetchGroupBannerListModel.payload.Page = pagination.currentPage;
    fetchGroupBannerListModel.payload.Limit = pagination.perPage;
    fetchGroupBannerListModel.payload.Filters = filters;
    fetchGroupBannerListModel.payload.Ordering = ordering;

    store
      .dispatch('api/apiRequest', fetchGroupBannerListModel)
      .then((response) => {
        groupBannerTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.api = false;
      });
  };

  const editGroupBanner = (
    groupBannerInformation,
    mappingBannerList,
    mappingSetBannerList
  ) => {
    editGroupBannerModel.payload.Group_Banner_Id =
      groupBannerInformation.group_banner_id;
    editGroupBannerModel.payload.Group_Name = groupBannerInformation.group_name;
    editGroupBannerModel.payload.Description =
      groupBannerInformation.description;
    editGroupBannerModel.payload.Status = groupBannerInformation.status ? 1 : 0;
    editGroupBannerModel.payload.Valid_From = groupBannerInformation.valid_from;
    editGroupBannerModel.payload.Valid_Through =
      groupBannerInformation.valid_through;
    editGroupBannerModel.payload.Banner_Mapping = mappingBannerList;
    editGroupBannerModel.payload.Banner_Mapping_Other_Set = mappingSetBannerList;

    store.dispatch('api/apiRequest', editGroupBannerModel).catch((err) => {
      openDefaultErrorModal(err);
    });
  };

  const resolveBannerStatusLabel = (status) => {
    return status ? 'Active' : 'Inactive';
  };

  const resolveBannerAction = (type) => {
    switch (type) {
      case 1:
        return;
      case 2:
        return;
      case 3:
        return;

      default:
        return;
    }
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchGroupBanner = (searchInput) => {
    searchValue.query = searchInput;
    onSearch();
  };

  const onExpandTable = (row) => {
    row.toggleDetails();

    selectedGroupBanner.row = row;
    selectedGroupBanner.index = row.index;
  };

  const removeMappingBanner = (removeBanner) => {
    if (removeBanner.banner_id) {
      groupBannerTable.payload[
        selectedGroupBanner.index
      ].mapping_banners = groupBannerTable.payload[
        selectedGroupBanner.index
      ].mapping_banners.filter(
        (mappedBanner) => mappedBanner.banner_id !== removeBanner.banner_id
      );
    }
    if (removeBanner.set_banner_id) {
      groupBannerTable.payload[
        selectedGroupBanner.index
      ].mapping_banners = groupBannerTable.payload[
        selectedGroupBanner.index
      ].mapping_banners.filter(
        (mappedBanner) =>
          mappedBanner.set_banner_id !== removeBanner.set_banner_id
      );
    }

    if (
      groupBannerTable.payload[selectedGroupBanner.index].mapping_banners
        .length <= 0
    ) {
      selectedGroupBanner.row.toggleDetails();
    }

    const {
      resolvedMappingBannerList,
      resolvedMappingSetBannerList,
    } = resolveMappingBannerList(
      groupBannerTable.payload[selectedGroupBanner.index].mapping_banners
    );

    editGroupBanner(
      groupBannerTable.payload[selectedGroupBanner.index].group_banner,
      resolvedMappingBannerList,
      resolvedMappingSetBannerList
    );
  };

  const resolveMappingBannerList = (mappingBannerList) => {
    const resolvedMappingBannerList = [];
    const resolvedMappingSetBannerList = [];

    const temptMappingBannerList = JSON.parse(
      JSON.stringify(mappingBannerList)
    );

    temptMappingBannerList.map((banner) => {
      if (banner.banner_id) {
        resolvedMappingBannerList.push({
          Seq: resolvedMappingBannerList.length + 1,
          Banner_Id: banner.banner_id,
        });
      }

      if (banner.set_banner_id) {
        resolvedMappingSetBannerList.push({
          Seq: resolvedMappingSetBannerList.length + 1,
          Set_Banner_Id: banner.set_banner_id,
        });
      }
    });

    return { resolvedMappingBannerList, resolvedMappingSetBannerList };
  };

  const onDeleteBannerGroup = (removeBannerGroup, buttonProperties) => {
    buttonProperties.loading = true;
    buttonProperties.disabled = true;

    deleteGroupBannerModel.payload.Group_Banner_Id =
      removeBannerGroup.group_banner.group_banner_id;

    store
      .dispatch('api/apiRequest', deleteGroupBannerModel)
      .then((response) => {
        groupBannerTable.payload.filter(
          (groupBanner) =>
            groupBanner.group_banner.group_banner_id !==
            removeBannerGroup.group_banner.group_banner_id
        );

        fetchGroupBannerList(false);
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        buttonProperties.loading = false;
        buttonProperties.disabled = false;
      });
  };

  const openDeleteWarningModal = (removeBannerGroup, buttonProperties) => {
    openWarningModal(
      'ลบ Banner Group',
      `ต้องการลบ ${removeBannerGroup.group_banner.group_name} หรือไม่`,
      () => onDeleteBannerGroup(removeBannerGroup, buttonProperties)
    );
  };

  onBeforeMount(() => {
    fetchGroupBannerList();
  });

  watch(
    [filters, ordering],
    () => {
      if (pagination.currentPage !== 1) {
        return (pagination.currentPage = 1);
      }

      return fetchGroupBannerList();
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.api) {
        fetchGroupBannerList();
      }
    },
    { deep: true }
  );

  return {
    fields,
    groupBannerTable,
    groupBannerActionList,
    pagination,
    searchValue,
    loading,
    resolveDateWithSeperator,
    resolveBannerStatusLabel,
    resolveBannerAction,
    onPageChange,
    onSearchGroupBanner,
    sortingChanged,
    removeMappingBanner,
    onExpandTable,
    onDeleteBannerGroup,
    openDeleteWarningModal,
    getPermissionModule
  };
}
